import { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react'
import _ from 'lodash'
import parse from 'date-fns/parse'
import { getBirthdayFromSsn } from 'common/src/vtj/ssn'
import { Block, Heading } from 'suomifi-ui-components'
import { Columns } from 'todistukset-ui/src/vtj/components/order-wizard-aet-shared/AetCommonFormComponents.sc'
import InfoItem from 'todistukset-ui/src/vtj/components/order-wizard-aet-shared/InfoItem.react'
import type { StepComponent } from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import { FormContainer } from 'todistukset-ui/src/vtj/components/order-wizard/CommonFormComponents.sc'
import LocalizedString, {
  LocalizedParagraphs,
} from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import { useUaetForm } from 'todistukset-ui/src/vtj/stores/uaet-form.store'
import { reversedDisplayNameOf } from 'todistukset-common/src/vtj/name.util'
import { finalEditableFieldValue } from 'todistukset-common/src/vtj/types/editable-field.type'
import {
  getDisplayValueForCountryCode,
  getDisplayValueForKansalaisuus,
} from 'todistukset-common/src/vtj/localization.util'
import { selectBirthPlaceByLang } from 'todistukset-common/src/vtj/birth-place.util'
import translateLanguage from 'common-translations/src/vtj/translate-language'
import { Divider } from 'todistukset-ui/src/vtj/components/order-wizard-aet-shared/AetOrderSummary/AetSummaryPriceInfo.sc'
import PriceInfoRow from 'todistukset-ui/src/vtj/components/order-wizard-aet-shared/AetOrderSummary/PriceInfoRow.react'
import { isFree } from 'todistukset-ui/src/vtj/components/order-wizard-uaet/uaet-price'
import {
  UaetSpouseAForm,
  UaetSpouseBForm,
} from 'todistukset-common/src/vtj/types/form-types.type'
import { TodistusPersonRole } from 'todistukset-common/src/vtj/types/todistus-person.type'
import { mailDeliveryFinalValues } from 'todistukset-common/src/vtj/mail-delivery.util'
import {
  DATE_FORMAT,
  formatDateToUiDateFormat,
} from 'todistukset-common/src/vtj/util/date.util'
import {
  getApostilleProduct,
  getEuVakiolomakeProduct,
} from 'todistukset-common/src/vtj/price.util'
import { formatEuroCentsIntoEurWithCommaFixedDecimalsIfNeeded } from 'payment-common/src/vtj/payment-v2.util'
import { CertificateProductType } from 'todistukset-common/src/vtj/types/price-list.type'

const UaetSummaryStep: StepComponent = observer(
  forwardRef(function UaetSummaryStep({ titleId }, _ref) {
    const {
      spouseA,
      spouseB,
      todistusLanguage,
      placeOfMarriage,
      includeApostille,
      includeTranslationForm,
      mailDelivery,
    } = useUaetForm()
    const { t } = useTranslation()

    const {
      common: { lang },
    } = useStore()

    if (!spouseA.hetu) {
      // This is for satisfying typescript, it should be checked long before we get here.
      throw new Error('UAET user needs hetu')
    }

    const birthdateSpouseA = formatDateToUiDateFormat(
      getBirthdayFromSsn(spouseA.hetu)
    )
    const birthPlaceSpouseA = spouseA.birthPlaceVTJ
      ? selectBirthPlaceByLang(
          spouseA.birthPlaceVTJ,
          finalEditableFieldValue(todistusLanguage) || lang
        )
      : null
    const birthdateSpouseBValue = finalEditableFieldValue(spouseB.birthdate)
    const birthdateSpouseB = birthdateSpouseBValue
      ? formatDateToUiDateFormat(
          parse(birthdateSpouseBValue, DATE_FORMAT, new Date())
        )
      : null
    const birthPlaceSpouseB = finalEditableFieldValue(spouseB.birthPlace)

    const placeOfMarriageValue = finalEditableFieldValue(placeOfMarriage)
    const translatedPlaceOfMarriage = placeOfMarriageValue
      ? getDisplayValueForCountryCode(placeOfMarriageValue, lang)
      : '-'

    const todistusLanguageValue = finalEditableFieldValue(todistusLanguage)
    const translatedTodistusLanguage = todistusLanguageValue
      ? translateLanguage({ code: todistusLanguageValue, lang })
      : '-'

    const includeTranslationFormValue =
      finalEditableFieldValue(includeTranslationForm) ?? false
    const includeApostilleValue =
      finalEditableFieldValue(includeApostille) ?? false

    const mailDeliveryValues = mailDelivery
      ? mailDeliveryFinalValues(mailDelivery)
      : null

    const selectedBooleanText = (b: boolean) =>
      b
        ? t('orderWizardOrderSummaryStepSelected')
        : t('orderWizardOrderSummaryStepNotSelected')

    return (
      <FormContainer>
        <Block variant="section">
          <Heading variant="h2" id={titleId}>
            <LocalizedString stringKey="orderWizardOrderSummaryStepMainTitle" />
          </Heading>
        </Block>
        <SpouseSection
          spouse={spouseA}
          birthdate={birthdateSpouseA}
          birthPlace={birthPlaceSpouseA}
        />
        <SpouseSection
          spouse={spouseB}
          birthdate={birthdateSpouseB || ''}
          birthPlace={birthPlaceSpouseB}
        />
        <Block variant="section">
          <Heading variant="h3">
            <LocalizedString stringKey="uaetSummaryAdditionalInfoTitle" />
          </Heading>
          <LocalizedParagraphs stringKey="uaetSummaryEditInstruction_paragraphs" />
          <Columns data-test-id="info-items-additional-info">
            <InfoItem
              title={t('aetSummaryTodistusLanguageLabel')}
              content={translatedTodistusLanguage}
            />
            <InfoItem
              title={t('aetSummaryPlaceOfMarriageLabel')}
              content={translatedPlaceOfMarriage}
            />
            <InfoItem
              title={t('orderWizardOrderSummaryStepEuTranslationForm')}
              content={selectedBooleanText(includeTranslationFormValue)}
            />
            <InfoItem
              title={t('orderWizardOrderSummaryStepApostille')}
              content={selectedBooleanText(includeApostilleValue)}
            />
          </Columns>
        </Block>
        <Block variant="section">
          <Heading variant="h3">
            <LocalizedString stringKey="aetSummaryDeliveryMethodTitle" />
          </Heading>
          <LocalizedString stringKey="orderWizardUaetInstructionsStepDeliveryText" />
          <div data-test-id="info-items-toimitustapa">
            {mailDeliveryValues && (
              <InfoItem
                title={t('orderWizardOrderSummaryStepPaperDeliveryAddress')}
                content={_.compact([
                  `${mailDeliveryValues.firstName} ${mailDeliveryValues.lastName}`,
                  mailDeliveryValues.streetAddress,
                  mailDeliveryValues.streetAddressExtras,
                  `${mailDeliveryValues.postalCode} ${mailDeliveryValues.postOffice}`,
                  mailDeliveryValues.country,
                ])}
              />
            )}
          </div>
        </Block>
        <Block variant="section">
          <div data-test-id="info-items-contact-info">
            <InfoItem
              title={t('aetSummaryContactInfoEmailLabel')}
              content={finalEditableFieldValue(spouseA.contactInfo.email) ?? ''}
            />
            <InfoItem
              title={t('aetSummaryContactInfoPhoneNumberLabel')}
              content={
                finalEditableFieldValue(spouseA.contactInfo.phoneNumber) ?? ''
              }
            />
          </div>
        </Block>
        <PriceSection
          includeApostille={includeApostilleValue}
          includeTranslationForm={includeTranslationFormValue}
        />
      </FormContainer>
    )
  })
)

const SpouseSection = observer(
  ({
    spouse,
    birthdate,
    birthPlace,
  }: {
    spouse: UaetSpouseAForm | UaetSpouseBForm
    birthdate: string
    birthPlace: string | null
  }) => {
    const {
      common: { lang },
    } = useStore()
    const { t } = useTranslation()

    const {
      personRole,
      firstNames,
      lastName,
      maritalStatus,
      citizenshipCode,
      birthCountryCode,
    } = spouse
    const maritalStatusValue = finalEditableFieldValue(maritalStatus)

    const displayedCitizenshipInfo = !_.isEmpty(
      finalEditableFieldValue(citizenshipCode)
    )
      ? getDisplayValueForKansalaisuus(citizenshipCode, lang)
      : '-'

    const displayedBirthCountry = finalEditableFieldValue(birthCountryCode)
      ? getDisplayValueForCountryCode(
          finalEditableFieldValue(birthCountryCode),
          lang
        )
      : '-'

    const displayedBirthPlace = birthPlace ?? '-'

    const title =
      personRole === TodistusPersonRole.PUOLISO_A
        ? 'aetSummaryOrdererInfoTitle'
        : 'aetSummarySpouseInfoTitle'

    const roleName =
      personRole === TodistusPersonRole.PUOLISO_A
        ? 'orderWizardOrderSummaryStepClient'
        : 'spouse'

    const testId =
      personRole === TodistusPersonRole.PUOLISO_A
        ? 'info-items-orderer-info'
        : 'info-items-spouse-info'

    return (
      <Block variant="section">
        <Heading variant="h3">
          <LocalizedString stringKey={title} />
        </Heading>
        {personRole === TodistusPersonRole.PUOLISO_B && (
          <LocalizedParagraphs stringKey="uaetSummaryEditInstruction_paragraphs" />
        )}
        <Columns data-test-id={testId}>
          <InfoItem
            title={t('aetOwnInfoNameLabel')}
            content={`${reversedDisplayNameOf({ lastName, firstNames })}`}
            extraInfo={`(${t(roleName)})`}
          />
          <InfoItem
            title={t('orderWizardPersonalInformationStepInputLabel_birthDate')}
            content={birthdate}
          />
          <InfoItem
            title={t('aetOwnInfoCitizenshipLabel')}
            content={displayedCitizenshipInfo}
          />
          <InfoItem
            title={t('orderWizardPersonalInformationStepInputLabel_birthPlace')}
            content={displayedBirthPlace}
          />
          <InfoItem title={t('birthCountry')} content={displayedBirthCountry} />
          <InfoItem
            title={t('aetOwnInfoMaritalStatusLabel')}
            content={t(`maritalStatus_${maritalStatusValue}`)}
          />
        </Columns>
      </Block>
    )
  }
)

const PriceSection = observer(
  ({
    includeApostille,
    includeTranslationForm,
  }: {
    includeApostille: boolean
    includeTranslationForm: boolean
  }) => {
    const { paymentPriceList } = useStore()
    const { t } = useTranslation()
    if (isFree({ includeApostille, includeTranslationForm })) {
      return (
        <Block variant="section" data-test-id="price-info-section">
          <Heading variant="h3">
            <LocalizedString stringKey="aetSummaryPriceInfoTitle" />
          </Heading>
          <PriceInfoRow
            leftContent={t('uaetSummaryPriceInfoUaet')}
            rightContent={t('aetSummaryPriceInfoNoFee')}
          />
          <Divider />
          <PriceInfoRow
            leftContent={t('aetSummaryPriceInfoTotal')}
            rightContent={t('aetSummaryPriceInfoNoFee')}
            bold
          />
        </Block>
      )
    } else {
      const products = _.compact([
        paymentPriceList.products.find(
          ({ productType }) =>
            productType === CertificateProductType.UAETTODISTUS
        ),
        includeTranslationForm
          ? getEuVakiolomakeProduct({
              priceList: paymentPriceList,
            })
          : null,
        includeApostille
          ? getApostilleProduct({
              priceList: paymentPriceList,
            })
          : null,
      ])
      const totalCost = _.sum(products.map((p) => p.priceWithVatInEuroCents))
      const formatCost = (cost: number) =>
        `${formatEuroCentsIntoEurWithCommaFixedDecimalsIfNeeded(cost)} €`

      return (
        <Block variant="section" data-test-id="price-info-section">
          <Heading variant="h3">
            <LocalizedString stringKey="aetSummaryPriceInfoTitle" />
          </Heading>
          {products.map((p) => (
            <PriceInfoRow
              key={p.productId}
              leftContent={t(`productTitleFor_${p.productType}`)}
              rightContent={formatCost(p.priceWithVatInEuroCents)}
            />
          ))}
          <Divider />
          <PriceInfoRow
            leftContent={t('aetSummaryPriceInfoTotal')}
            rightContent={formatCost(totalCost)}
            bold
          />
        </Block>
      )
    }
  }
)

export default UaetSummaryStep
